import { useEffect } from "react";
import { useFormContext } from "@jsq/ds/forms";
import { DocumentData } from "../../components/documents/DocumentCollector/types";
import { DocumentFormValues } from "./useDocumentProcessor";

/**
 * Hook for tracking unsaved changes in document forms.
 *
 * This hook monitors form values and compares them against initial files to detect:
 * - New file uploads (files with lastModified property)
 * - File removals (original file exists but current file is null)
 * - File replacements (original file exists and new file uploaded)
 * - Guest document deletions
 *
 * @param initialFiles - Array of original document data for comparison
 * @param deletedIdsOfGuest - Array of guest document IDs that have been deleted
 * @param onUnsavedChangesChange - Optional callback to notify parent component of changes
 */
export default function useUnsavedChangesTracker(
  initialFiles: DocumentData[],
  deletedIdsOfGuest: number[],
  onUnsavedChangesChange?: (hasChanges: boolean) => void,
) {
  const formContext = useFormContext<DocumentFormValues>();

  useEffect(() => {
    if (!onUnsavedChangesChange) return;

    const hasChanges =
      formContext.values.documents.some((document, index) => {
        const originalFile = initialFiles[index]?.file;
        const currentFile = document.file;

        // Check if an existing file was removed
        const wasFileRemoved = originalFile && !currentFile;

        // Check if a new file was uploaded (has lastModified property and wasn't in original files)
        const isNewFile = currentFile && !!currentFile.file?.lastModified && !originalFile;

        // Check if an existing file was replaced with a new one
        const wasFileReplaced =
          originalFile &&
          currentFile &&
          !!currentFile.file?.lastModified &&
          originalFile.uuid !== currentFile.uuid;

        return isNewFile || wasFileRemoved || wasFileReplaced;
      }) || deletedIdsOfGuest.length > 0; // Include guest document deletions

    onUnsavedChangesChange(hasChanges);
  }, [formContext.values, deletedIdsOfGuest, initialFiles, onUnsavedChangesChange]);
}
